import React, { useState, useCallback, useEffect } from 'react';
import ReactPlayer from 'react-player';
import {
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { validateVimeoUrl } from 'utils/vimeo-utils';

interface VimeoPreviewProps {
  url: string;
  width?: string | number;
  height?: string | number;
  showControls?: boolean;
  autoPlay?: boolean;
  onError?: (error: string) => void;
  onReady?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const VimeoPreview: React.FC<VimeoPreviewProps> = ({
  url,
  width = '100%',
  height = 'auto',
  showControls = false,
  autoPlay = false,
  onError,
  onReady,
  className,
  style,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const urlInfo = validateVimeoUrl(url);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setErrorMessage('');
  }, [url]);

  const handleReady = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
    onReady?.();
  }, [onReady]);

  const handleError = useCallback((error: any) => {
    setIsLoading(false);

    const isActualVideoError = error instanceof Error;

    if (isActualVideoError) {
      setHasError(true);
      const errorMsg = 'Failed to load Vimeo video. Please check the URL and try again.';
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
    } else {
      onError?.(error?.message || 'Internal player error');
    }
  }, [onError]);

  const handleLoadStart = useCallback(() => {
    setIsLoading(true);
    setHasError(false);
  }, []);

  const handleProgress = useCallback(() => {
    if (isLoading) {
      setIsLoading(false);
    }
  }, [isLoading]);

  const handlePlay = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  if (!urlInfo.isValid) {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #ccc',
          borderRadius: 1,
          backgroundColor: '#f5f5f5',
          ...style,
        }}
        className={className}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          Invalid Vimeo URL. Please enter a valid Vimeo URL.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width,
        height,
        position: 'relative',
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: '#000',
        ...style,
      }}
      className={className}
    >
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 2,
          }}
        >
          <CircularProgress size={40} sx={{ color: 'white' }} />
        </Box>
      )}

      {hasError && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            padding: 2,
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {errorMessage}
          </Alert>
        </Box>
      )}

      <ReactPlayer
        url={urlInfo.normalizedUrl || url}
        width="100%"
        height="100%"
        controls={showControls}
        playing={autoPlay}
        onReady={handleReady}
        onError={handleError}
        onLoadStart={handleLoadStart}
        onProgress={handleProgress}
        onPlay={handlePlay}
        config={{
          vimeo: {
            playerOptions: {
              responsive: true,
            },
          },
        }}
        style={{
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      />
    </Box>
  );
};

VimeoPreview.defaultProps = {
  width: '100%',
  height: 'auto',
  showControls: false,
  autoPlay: false,
  onError: undefined,
  onReady: undefined,
  className: undefined,
  style: undefined,
};

export default VimeoPreview;
